/**
 * The Stage-1 route table.
 *
 * A data router (react-router 7 `createBrowserRouter`) so later stages can attach
 * loaders/actions per route without restructuring. The root route renders <App/> — the ONE
 * consistent frame (PC-F06) — and every child renders inside its <Outlet/>.
 *
 * Every entry in PRIMARY_NAV (PC-F10) has a matching route here so no nav link dead-ends.
 * The child surfaces are deliberately empty placeholders for now:
 *
 *  PC-F14  A named future capability appears NOWHERE — no "coming soon", no disabled
 *          button, no invented feature name. The nav destinations themselves are required
 *          by PC-F10, so a neutral "part of a later build stage" note on a destination that
 *          already exists is fine; naming a capability that does not exist yet is not.
 */
import { createBrowserRouter } from 'react-router';

import { App } from './App';
import { PersonProfile, personLoader } from './people/PersonProfile';
import {
  Profile,
  type MeLoaderData,
  type MeResponse,
  type ProfileToolSummary,
  type MyBadge,
} from './Profile';
import type { FollowingEntry } from './community/api';
import { Catalogue, catalogueLoader } from './tools/Catalogue';
import { Share } from './tools/Share';
import { UploadPreview } from './tools/upload-preview/UploadPreview';
import { BadgePreview } from './design/BadgePreview';
import { ToolDetail, toolDetailLoader } from './tools/ToolDetail';
import { Community, communityLoader } from './community/Community';
import { Channel, channelLoader } from './community/Channel';
import { ThreadView, threadLoader } from './community/ThreadView';
import { Notifications, notificationsLoader } from './notifications/Notifications';
import { Settings, settingsLoader } from './Settings';
import { LearningHub, learningHubLoader } from './learning/LearningHub';
import { AdminHome, adminHomeLoader } from './admin/AdminHome';
import { Appointments, appointmentsLoader } from './admin/Appointments';
import { Teams, teamsLoader } from './admin/Teams';
import { LearningMetrics, learningMetricsLoader } from './admin/LearningMetrics';
import { ToolGovernance, toolGovernanceLoader } from './admin/ToolGovernance';
import { PlatformSettings, platformSettingsLoader } from './admin/PlatformSettings';
import { AccessRequirements, accessRequirementsLoader } from './admin/AccessRequirements';
import { Headcount, headcountLoader } from './admin/Headcount';
import { RecentSignups, recentSignupsLoader } from './admin/RecentSignups';
import { TermsOfUseAdmin, termsAdminLoader } from './admin/TermsOfUse';
// The reviewer queue for PENDING TOOL SUBMISSIONS (UP-F25). The Community Hub's own forum-post
// moderation used to have a second, duplicate `ReviewQueue` component at community/ReviewQueue —
// retired in favour of the one moderation queue at /moderation, which already handled both content
// kinds — so this import no longer needs the alias that disambiguated the two.
import { ReviewQueue, reviewQueueLoader } from './admin/ReviewQueue';
import { ModerationQueue, moderationQueueLoader } from './admin/ModerationQueue';
import { SupportQueue, supportQueueLoader } from './admin/SupportQueue';
import { DashboardLayout, dashboardLayoutLoader } from './dashboard/DashboardLayout';
import { Overview } from './dashboard/Overview';
import { Tooling } from './dashboard/Tooling';
import { Learning as DashboardLearning } from './dashboard/Learning';
import { overviewLoader, toolingLoader, learningLoader } from './dashboard/api';

/**
 * The /learning/tour guided-platform-tour placeholder. The "How to use the platform" intent tile on
 * the Learning Hub (decision #6) points here. The tour itself is being built elsewhere; until it
 * lands this renders a neutral "part of a later build stage" note rather than a dead link or an
 * invented capability name (PC-F14 — the destination genuinely exists; only its content is pending).
 */
function GuidanceTour() {
  return (
    <section aria-labelledby="tour-heading">
      <h1 id="tour-heading">How to use the platform</h1>
      <p style={{ maxWidth: '60ch' }}>
        A guided tour of Trailblazer — how to find, build, share and adopt tools — is part of a
        later build stage. In the meantime, the <a href="/learning">Learning Hub</a> links out to
        Microsoft&rsquo;s own Power Automate learning site, and every section of the app carries its
        own in-page guidance.
      </p>
    </section>
  );
}

/**
 * The /me loader. Asks OUR backend who the caller is (GET /api/me) and, for a signed-in owner,
 * their own tool lists (GET /api/me/tools, GET /api/me/adopted) — all same-origin via the Vite
 * proxy, sending the app session cookie. A 401 on /api/me is the normal "not signed in yet"
 * answer — we surface it as a signed-out marker so the page shows a Sign-in prompt, NOT an error.
 * Any other non-OK status is a genuine fault and is thrown to the route error boundary.
 *
 * The tool lists are the OWNER's own private view — they exist only under /api/me/*. The browser
 * never talks to Microsoft here; it only ever calls our gateway.
 */
async function meLoader(): Promise<MeLoaderData> {
  const res = await fetch('/api/me', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (res.status === 401) return { signedIn: false };
  if (!res.ok) throw new Error(`GET /api/me failed with status ${res.status}`);
  const me = (await res.json()) as MeResponse;

  // The owner's own tool lists + earned badges + the follow graph. Fetched in parallel; tools return
  // { tools: [...] }, badges returns { badges: [...] }, following returns { following: [...],
  // followerCount } — all owner-private (see /api/me/following: the "who I follow" list, PR-F61, plus
  // the owner's own follower COUNT, PR-F52; never who those followers are).
  const [sharedRes, adoptedRes, savedRes, badgesRes, followingRes] = await Promise.all([
    fetch('/api/me/tools', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }),
    fetch('/api/me/adopted', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }),
    fetch('/api/me/tools/saved', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }),
    fetch('/api/me/badges', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }),
    fetch('/api/me/following', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }),
  ]);
  if (!sharedRes.ok) throw new Error(`GET /api/me/tools failed with status ${sharedRes.status}`);
  if (!adoptedRes.ok)
    throw new Error(`GET /api/me/adopted failed with status ${adoptedRes.status}`);
  if (!savedRes.ok)
    throw new Error(`GET /api/me/tools/saved failed with status ${savedRes.status}`);
  if (!badgesRes.ok) throw new Error(`GET /api/me/badges failed with status ${badgesRes.status}`);
  if (!followingRes.ok)
    throw new Error(`GET /api/me/following failed with status ${followingRes.status}`);
  const shared = ((await sharedRes.json()) as { tools: ProfileToolSummary[] }).tools;
  const adopted = ((await adoptedRes.json()) as { tools: ProfileToolSummary[] }).tools;
  const saved = ((await savedRes.json()) as { tools: ProfileToolSummary[] }).tools;
  const badges = ((await badgesRes.json()) as { badges: MyBadge[] }).badges;
  const followingBody = (await followingRes.json()) as {
    following: FollowingEntry[];
    followerCount: number;
  };

  return {
    signedIn: true,
    me,
    shared,
    adopted,
    saved,
    badges,
    followerCount: followingBody.followerCount,
    following: followingBody.following,
  };
}

export const router = createBrowserRouter([
  // Production Kombi homepage. It is a top-level lazy route so no legacy frame, footer, terms gate
  // or route transition can duplicate the live surface. The implementation is shared with the
  // private live reference; only its safe authentication return destination differs.
  {
    path: '/',
    lazy: async () => {
      const module = await import('./design/KombiLive');
      return {
        Component: module.KombiLiveProduction,
        loader: module.kombiLiveLoaderFor(module.KOMBI_LIVE_ROUTES.production),
      };
    },
  },
  // The three approved Kombi catalogue categories are static, lazy routes. They stay outside the
  // legacy frame and use the same live session/terms boundary as `/` and `/tools/:toolId`.
  {
    path: '/catalogue/ai-agents',
    lazy: async () => {
      const module = await import('./design/KombiCatalogue');
      return {
        Component: module.KombiAIAgentsCatalogue,
        loader: module.kombiCatalogueLoaderFor('aiAgents', module.KOMBI_LIVE_ROUTES.aiAgents),
      };
    },
  },
  {
    path: '/catalogue/apps',
    lazy: async () => {
      const module = await import('./design/KombiCatalogue');
      return {
        Component: module.KombiAppsCatalogue,
        loader: module.kombiCatalogueLoaderFor('apps', module.KOMBI_LIVE_ROUTES.apps),
      };
    },
  },
  {
    path: '/catalogue/automations',
    lazy: async () => {
      const module = await import('./design/KombiCatalogue');
      return {
        Component: module.KombiAutomationsCatalogue,
        loader: module.kombiCatalogueLoaderFor('automations', module.KOMBI_LIVE_ROUTES.automations),
      };
    },
  },
  // Admin Tools (ROLE_GATED_NAV — any of Reviewer/ChampionRole/SWATMember/PlatformOwner). A Kombi
  // surface, so a top-level route outside the legacy frame like `/` and the catalogue three. It keeps
  // the `/admin` URL the nav has always pointed at, so no reference needed repointing; the previous
  // teal page is unchanged at `/admin/legacy`, and the eleven sub-routes below are untouched.
  // The Learning Hub (LH-F07..F16) — a Kombi surface, so a top-level route outside the legacy frame
  // like `/`, `/admin` and the catalogue three. It keeps the `/learning` URL PRIMARY_NAV has always
  // pointed at (PC-F10 pins that nav at exactly seven entries and Learning is one of them), so no
  // reference needed repointing. The previous half-converted page is unchanged at `/learning/legacy`,
  // and `/learning/tour` stays inside the legacy frame below — it is a sibling path, not a child of
  // this route, so promoting this one does not orphan it.
  {
    path: '/learning',
    lazy: async () => {
      const module = await import('./design/KombiLearning');
      return { Component: module.KombiLearning, loader: module.kombiLearningLoader };
    },
  },
  {
    path: '/admin',
    lazy: async () => {
      const module = await import('./design/KombiAdmin');
      return {
        Component: module.KombiAdminProduction,
        loader: module.kombiAdminLoaderFor(module.KOMBI_LIVE_ROUTES.admin),
      };
    },
  },
  {
    path: '/admin/branding',
    lazy: async () => {
      const module = await import('./design/KombiBranding');
      return {
        Component: module.KombiBrandingOverview,
      };
    },
  },
  {
    path: '/admin/branding/new',
    lazy: async () => {
      const module = await import('./design/KombiBranding');
      return { Component: module.KombiBrandingNew, loader: module.kombiBrandingNewLoader };
    },
  },
  {
    path: '/admin/branding/:presetId/edit',
    lazy: async () => {
      const module = await import('./design/KombiBranding');
      return { Component: module.KombiBrandingEdit, loader: module.kombiBrandingEditLoader };
    },
  },
  // Profile (/me) — promoted to the Kombi surface, a top-level route outside the legacy frame exactly
  // like `/admin`. Its implementation is shared with the private `/design/kombi-profile-live` reference;
  // only its safe authentication return destination differs. It keeps the `/me` URL every profile link
  // (PRIMARY_NAV, notifications, learning, colleague "view your profile") already points at, so nothing
  // needed repointing; the previous page is unchanged at `me/legacy`.
  {
    path: '/me',
    lazy: async () => {
      const module = await import('./design/KombiProfileLive');
      return {
        Component: module.KombiProfileProduction,
        loader: module.kombiProfileLoaderFor(module.KOMBI_LIVE_ROUTES.profile),
      };
    },
  },
  // The Manager Metrics Dashboard (/dashboard, MD-F##) — promoted to the Kombi surface, a top-level
  // route outside the legacy frame like `/admin`. The Kombi header's "Dashboard" entry (Managers only)
  // already pointed here, so this makes that link land on the Kombi page rather than the old teal
  // three-tab layout. Shared with the private `/design/kombi-manager-dashboard-live` reference; only the
  // return destination differs. The previous nested layout is unchanged at `dashboard/legacy`.
  {
    path: '/dashboard',
    lazy: async () => {
      const module = await import('./design/KombiManagerDashboardLive');
      return {
        Component: module.KombiManagerDashboardProduction,
        loader: module.kombiManagerDashboardLoaderFor(module.KOMBI_LIVE_ROUTES.managerDashboard),
      };
    },
  },
  // Community is now the live Kombi surface, top-level so the legacy Trailblazer frame cannot add a
  // second header or page inset. The old landing remains available at /community/legacy; channel and
  // thread deep links stay below in the legacy frame and retain their URLs.
  {
    path: '/community',
    lazy: async () => {
      const module = await import('./design/KombiCommunityLive');
      return {
        Component: module.KombiCommunityProduction,
        loader: module.kombiCommunityLoaderFor(module.KOMBI_LIVE_ROUTES.community),
      };
    },
  },
  // The TOOL DETAIL page — a Kombi surface, so a top-level route outside the legacy frame like `/`,
  // `/admin`, `/learning`, `/share` and the catalogue three. It keeps the `/tools/:toolId` URL that the
  // catalogue cards, the search results, the profile galleries and the kind-1 publish announcement have
  // always pointed at, so nothing needed repointing. The previous page is unchanged at
  // `tools/:toolId/legacy`, which is also where this page's "report an issue" hands off to — that flow
  // has no approved Kombi treatment yet.
  //
  // Same component as the private `/design/kombi-automation-detail-live/:toolId` preview below, on its
  // own loader and route map — see KombiAutomationDetailLive.tsx for why it is parameterised rather
  // than registered twice.
  {
    path: '/tools/:toolId',
    lazy: async () => {
      const module = await import('./design/KombiAutomationDetailLive');
      return { Component: module.KombiToolDetail, loader: module.kombiToolDetailLoader };
    },
  },
  {
    path: '/catalogue/automations/:toolId/edit',
    lazy: async () => {
      const module = await import('./design/KombiToolEdit');
      return { Component: module.KombiToolEdit, loader: module.kombiToolEditLoader };
    },
  },
  // The canonical colleague profile is a Kombi surface. Keeping it top-level prevents the legacy
  // App frame from adding a second header or an inherited content inset on direct links and refreshes.
  {
    path: '/users/:userId',
    lazy: async () => {
      const module = await import('./design/KombiPublicProfileLive');
      return {
        Component: module.KombiPublicProfileProduction,
        loader: module.kombiCanonicalPublicProfileLoader,
      };
    },
  },
  // The "share an automation" journey (UP-F01) — four Kombi surfaces, so four TOP-LEVEL routes outside
  // the legacy frame, like `/`, `/admin`, `/learning` and the catalogue three. It keeps `/share` as the
  // journey's ENTRY (the Select step), so the catalogue's "Share what you've built" link, PRIMARY_NAV's
  // 'Share a tool' entry and the guided tour's selector all still land in the right place with nothing
  // repointed. The legacy upload overlay is unchanged at `share/legacy`; `share/preview` is a sibling
  // path below, not a child, so promoting these does not orphan it.
  //
  // Each shares ONE loader (a single /api/me probe, no protected fetch) and one boundary — see
  // design/KombiShare.tsx.
  {
    path: '/share',
    lazy: async () => {
      const module = await import('./design/KombiShare');
      return { Component: module.KombiShareSelect, loader: module.kombiShareLoader };
    },
  },
  {
    path: '/share/context',
    lazy: async () => {
      const module = await import('./design/KombiShare');
      return { Component: module.KombiShareContext, loader: module.kombiShareLoader };
    },
  },
  {
    path: '/share/review',
    lazy: async () => {
      const module = await import('./design/KombiShare');
      return { Component: module.KombiShareReview, loader: module.kombiShareLoader };
    },
  },
  {
    path: '/share/submitted',
    lazy: async () => {
      const module = await import('./design/KombiShare');
      return { Component: module.KombiShareSubmitted, loader: module.kombiShareLoader };
    },
  },
  {
    // Every non-home route remains inside the current Trailblazer frame. A pathless parent retains
    // their existing root-relative URLs while making `/` exclusively the Kombi production route.
    element: <App />,
    children: [
      // Stage 3.5: the Dataverse upload/adopt surfaces. These call our gateway's /api/tools and
      // /api/flows routes (mounted only when FEATURE_DATAVERSE_ARTEFACT is on); each loader treats a
      // 404 on those routes as "feature disabled" and degrades gracefully rather than dead-ending.
      { path: 'tools', element: <Catalogue />, loader: catalogueLoader },
      // The PREVIOUS tool detail page. `/tools/:toolId` itself is now the Kombi surface (a top-level
      // route above, outside this frame), exactly as `learning/legacy`, `admin/legacy` and
      // `share/legacy` were moved. This page's code is UNCHANGED, it stays reachable for comparison and
      // rollback, and it is the live destination for "report an issue" — that flow has no approved
      // Kombi treatment yet, so the Kombi page links here rather than offering a dead control.
      { path: 'tools/:toolId/legacy', element: <ToolDetail />, loader: toolDetailLoader },
      // The PREVIOUS upload surface — the legacy overlay with its three skins. `/share` itself is now
      // the Kombi journey (four top-level routes above, outside this frame — see KombiShare.tsx); this
      // page's code is UNCHANGED and stays reachable here for comparison and rollback, exactly as
      // `learning/legacy` and `admin/legacy` do. No loader: ShareOverlay loads its own data on open
      // behind a brief in-overlay spinner, so the route frame appears immediately.
      { path: 'share/legacy', element: <Share /> },
      // Design preview for the Tool Upload journey (design/Tool Upload*.svg). Fixtures only, no
      // loader and no API, so it renders with the backend down and can be reviewed for fidelity on
      // its own. Not a product surface: it is absent from PRIMARY_NAV and carries no capability
      // claim (PC-F14). See docs/design-build/tool-upload/.
      { path: 'share/preview', element: <UploadPreview /> },
      // Design preview for the badge medallions (design/BadgeIcon.tsx). Same standing as the two above.
      { path: 'design/badges', element: <BadgePreview /> },
      // The PREVIOUS Learning Hub — the half-converted page (Kombi wrapper, legacy header, Tailwind
      // buttons, its own imitation stylesheet). `/learning` itself is now the Kombi surface (a
      // top-level route above, outside this frame — see KombiLearning.tsx); this page's code is
      // UNCHANGED and stays reachable here for comparison and rollback. One loader call hydrates it;
      // it is cache-only server-side so it never blocks on / fails because of Microsoft Learn
      // (LH-F03/LH-NFR01). A 401 renders the signed-out prompt; other non-OK go to the error boundary.
      { path: 'learning/legacy', element: <LearningHub />, loader: learningHubLoader },
      // The "How to use the platform" intent tile's destination — a guided platform tour being built
      // elsewhere; a neutral provisional placeholder for now (no loader, no API).
      { path: 'learning/tour', element: <GuidanceTour /> },
      // Community Hub (forum) — a core, always-on family. The three surfaces (landing, channel,
      // thread) each load from /api/community; the backend gates read/post against live
      // appointments, so a restricted channel simply never appears / 404s (CH-F15). Provisional UI.
      { path: 'community/legacy', element: <Community />, loader: communityLoader },
      { path: 'community/thread/:threadId', element: <ThreadView />, loader: threadLoader },
      // The Community Hub used to have its OWN forum-post moderation queue at this path
      // (community/review-queue) — a second, duplicate implementation of what /moderation already
      // does for both forum posts and tool comments. Retired; Community.tsx's "Review queue" link
      // now points at /moderation instead. No route registered here any more.
      { path: 'community/:channelSlug', element: <Channel />, loader: channelLoader },
      // The PREVIOUS profile page. `/me` itself is now the Kombi surface (a top-level route above,
      // outside this frame — see KombiProfileLive.tsx); this page's code is UNCHANGED and stays
      // reachable here for comparison and rollback, exactly as `admin/legacy` and `learning/legacy` do.
      { path: 'me/legacy', element: <Profile />, loader: meLoader },
      //  - /people/:userId (PersonProfile): opened from a person suggestion in the global search
      //    field (DS-F21). Renders only §3.2-public fields; its API cannot return a private one.
      { path: 'people/:userId', element: <PersonProfile />, loader: personLoader },
      { path: 'notifications', element: <Notifications />, loader: notificationsLoader },
      // The person's own settings surface. Reached by links on /me, NOT from PRIMARY_NAV — PC-F10
      // pins the primary nav at exactly seven entries, and FR8's profile menu does not exist. It
      // ships with the guided tour's replay control and nothing else (see Settings.tsx); distinct
      // from /admin/settings, which is the PlatformOwner's platform-wide settings (PC-F20).
      { path: 'settings', element: <Settings />, loader: settingsLoader },
      // The PREVIOUS Admin Tools landing page, on the teal design system. `/admin` itself is now the
      // Kombi surface (a top-level route above, outside this frame — see KombiAdmin.tsx); this page's
      // code is UNCHANGED and stays reachable here for comparison and rollback. Both index the same
      // eleven sub-routes below, which are untouched and still independently routable (PC-F08).
      { path: 'admin/legacy', element: <AdminHome />, loader: adminHomeLoader },
      // PlatformOwner-only appointment management (PC-F19), linked from the Admin Tools landing
      // page's PlatformOwner section.
      { path: 'admin/appointments', element: <Appointments />, loader: appointmentsLoader },
      // PlatformOwner-only team management (team feature, 0049), linked from the Admin Tools landing
      // page's Governance section. A team records who its manager/champions are against their live
      // appointments; it never grants a role. Loader treats 403 as NotPermitted in-page.
      { path: 'admin/teams', element: <Teams />, loader: teamsLoader },
      // Manager/PlatformOwner-only per-resource learning-click metrics (MD-F##), linked from the
      // Admin Tools landing page. Aggregate-only, cohort-suppressed; loader treats 403 as
      // NotPermitted in-page (a Manager reaching it by direct URL without the appointment).
      {
        path: 'admin/learning-metrics',
        element: <LearningMetrics />,
        loader: learningMetricsLoader,
      },
      // Catalogue governance triage (TD-F23) — the actual actions live on each tool's own detail
      // page (OwnerActionsPanel); this is the "what's flagged" index only.
      { path: 'admin/tools', element: <ToolGovernance />, loader: toolGovernanceLoader },
      // Platform-settings editing (PC-F20), linked from the Admin Tools landing page.
      { path: 'admin/settings', element: <PlatformSettings />, loader: platformSettingsLoader },
      // PlatformOwner-only editing of the ACCESS value list (CAT-F13, CAT-F14, CAT-NFR04). Not
      // folded into Platform Settings despite both being owner-only value editing: a setting is a
      // number somebody tunes, whereas a row here decides who can see which tools, and its rule
      // editor has to state a consequence before it will save. Loader maps a 403 to an in-page
      // NotPermitted, like the others, since a non-owner can reach this by a direct URL.
      {
        path: 'admin/access-requirements',
        element: <AccessRequirements />,
        loader: accessRequirementsLoader,
      },
      // PlatformOwner-only recording of the workforce size (MD-F04), which the manager dashboard's
      // reach figure divides by (MD-F07). Its own page rather than a platform setting because
      // `app.headcount` is keyed by scope and dated: 'ALL' and each department each need a figure, and
      // reach needs to know how stale the one it divided by is. Owner-only, 403 mapped in-page.
      { path: 'admin/headcount', element: <Headcount />, loader: headcountLoader },
      // PlatformOwner-only read-only widget listing recent registrations (UR-F05), linked from the
      // Admin Tools Governance section. Read-only; the panel's loader maps a 403 to an in-page
      // NotPermitted, like the others, since a non-owner can reach this by a direct URL.
      { path: 'admin/sign-ups', element: <RecentSignups />, loader: recentSignupsLoader },
      // PlatformOwner-only Terms of Use publishing (TU-F10), linked from the Admin Tools landing
      // page. Loader maps a 403 to an in-page NotPermitted (a non-owner reaching it by direct URL).
      { path: 'admin/terms', element: <TermsOfUseAdmin />, loader: termsAdminLoader },
      // The reviewer/moderator/SWAT queues, still individually routable (bookmarks, direct links)
      // even though they no longer have their own ROLE_GATED_NAV entry. Each loader treats a 403 as
      // "you don't hold this appointment" and renders it in-page (NotPermitted) rather than
      // throwing, since a caller can reach these by a direct URL even when the nav link is hidden.
      { path: 'review', element: <ReviewQueue />, loader: reviewQueueLoader },
      { path: 'moderation', element: <ModerationQueue />, loader: moderationQueueLoader },
      { path: 'support-queue', element: <SupportQueue />, loader: supportQueueLoader },
      // The PREVIOUS Manager Metrics Dashboard (MD-F##) — the teal nested layout: the parent renders
      // the tab bar (Overview | Tooling | Learning) + the shared period/department filter bar; each
      // child page has its own loader that fetches its metrics and maps the in-handler
      // Manager|PlatformOwner gate's 403 to an in-page NotPermitted. `/dashboard` itself is now the
      // Kombi surface (a top-level route above, outside this frame — see KombiManagerDashboardLive.tsx);
      // this page's code is UNCHANGED and stays reachable here for comparison and rollback, exactly as
      // `admin/legacy` and `learning/legacy` do. Its own tab links point at `dashboard/legacy/*`.
      {
        path: 'dashboard/legacy',
        element: <DashboardLayout />,
        loader: dashboardLayoutLoader,
        children: [
          { index: true, element: <Overview />, loader: overviewLoader },
          { path: 'tooling', element: <Tooling />, loader: toolingLoader },
          { path: 'learning', element: <DashboardLearning />, loader: learningLoader },
        ],
      },
    ],
  },
  // Private visual-parity route. A top-level sibling is required so the exact Kombi homepage
  // renders without Trailblazer's legacy frame, footer, terms gate, or route transitions.
  {
    path: '/design/kombi',
    lazy: async () => ({ Component: (await import('./design/KombiPreview')).KombiPreview }),
  },
  {
    path: '/design/kombi-live',
    lazy: async () => {
      const module = await import('./design/KombiLive');
      return { Component: module.KombiLive, loader: module.kombiLiveLoader };
    },
  },
  // Private, fixture-only terms-gate harness. It intentionally has no loader or API dependency.
  {
    path: '/design/kombi-terms',
    lazy: async () => ({ Component: (await import('./design/KombiTerms')).KombiTerms }),
  },
  {
    path: '/design/kombi-profile',
    lazy: async () => ({ Component: (await import('./design/KombiProfile')).KombiProfile }),
  },
  {
    path: '/design/kombi-profile-live',
    lazy: async () => {
      const module = await import('./design/KombiProfileLive');
      return { Component: module.KombiProfileLive, loader: module.kombiProfileLiveLoader };
    },
  },
  {
    path: '/design/kombi-public-profile/:userId',
    lazy: async () => ({
      Component: (await import('./design/KombiPublicProfile')).KombiPublicProfileFixture,
    }),
  },
  {
    path: '/design/kombi-public-profile-live/:userId',
    lazy: async () => {
      const module = await import('./design/KombiPublicProfileLive');
      return { Component: module.KombiPublicProfileLive, loader: module.kombiPublicProfileLoader };
    },
  },
  {
    path: '/design/kombi-community',
    lazy: async () => ({
      Component: (await import('./design/KombiCommunity')).KombiCommunity,
    }),
  },
  {
    path: '/design/kombi-community-live',
    lazy: async () => {
      const module = await import('./design/KombiCommunityLive');
      return { Component: module.KombiCommunityLive, loader: module.kombiCommunityLiveLoader };
    },
  },
  {
    path: '/design/kombi-catalogue/:category',
    lazy: async () => ({
      Component: (await import('./design/KombiFixtureCatalogue')).KombiFixtureCatalogue,
    }),
  },
  {
    path: '/design/kombi-catalogue-live/:category',
    lazy: async () => {
      const module = await import('./design/KombiLivePreviewCatalogue');
      return {
        Component: module.KombiLivePreviewCatalogue,
        loader: module.kombiLivePreviewCatalogueLoader,
      };
    },
  },
  {
    path: '/design/kombi-manager-dashboard',
    lazy: async () => ({
      Component: (await import('./design/KombiManagerDashboard')).KombiManagerDashboard,
    }),
  },
  {
    path: '/design/kombi-manager-dashboard-live',
    lazy: async () => {
      const module = await import('./design/KombiManagerDashboardLive');
      return {
        Component: module.KombiManagerDashboardLive,
        loader: module.kombiManagerDashboardLiveLoader,
      };
    },
  },
  // Private, API-free fixture for the approved Kombi automation-detail design. It is deliberately
  // separate from the catalogue and from the live `/tools/:toolId` page: this one is FIXTURE-backed and
  // makes no API call, so it renders with the backend down.
  {
    path: '/design/kombi-automation-detail/:slug',
    lazy: async () => ({
      Component: (await import('./design/KombiAutomationDetail')).KombiAutomationDetail,
    }),
  },
  {
    path: '/design/kombi-tool/:slug',
    lazy: async () => ({
      Component: (await import('./design/KombiFixtureToolDetail')).KombiFixtureToolDetail,
    }),
  },
  // Private live counterpart of the fixture route. Production catalogue/search links remain unchanged.
  {
    path: '/design/kombi-automation-detail-live/:toolId',
    lazy: async () => {
      const module = await import('./design/KombiAutomationDetailLive');
      return {
        Component: module.KombiAutomationDetailLive,
        loader: module.kombiAutomationDetailLiveLoader,
      };
    },
  },
  // Private, fixture-only automation-share journey. It is deliberately absent from product navigation.
  {
    path: '/design/kombi-automation-share/select',
    lazy: async () => ({
      Component: (await import('./design/KombiAutomationShare')).KombiAutomationSelect,
    }),
  },
  {
    path: '/design/kombi-automation-share/context',
    lazy: async () => ({
      Component: (await import('./design/KombiAutomationShare')).KombiAutomationContext,
    }),
  },
  {
    path: '/design/kombi-automation-share/review',
    lazy: async () => ({
      Component: (await import('./design/KombiAutomationShare')).KombiAutomationReview,
    }),
  },
  {
    path: '/design/kombi-automation-share/submitted',
    lazy: async () => ({
      Component: (await import('./design/KombiAutomationShare')).KombiAutomationSubmitted,
    }),
  },
]);
